import { useAppDispatch, useAppSelector } from "app/hooks";
import {
    selectFormType,
    selectEvaluationFormType,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import {
    ScorableQuestionGroupList,
    ScorableQuestionGroupListSurveys,
} from "../../scorable/Scorable";
import { GuxButton, GuxIcon } from "genesys-spark-components-react";
import Footer from "features/qualityForm/formViewer/footer/Footer";
import strings from "./FormViewerPreview.strings";
import { useStrings } from "features/i18n/useStrings";
import "./FormViewerPreview.less";
import {
    getPreviewScores,
    selectAgentHasRead,
    selectIsScorableComplete,
} from "features/scorable/scorableSlice";
import toastService from "services/toastService";
import {
    scorableSliceActions,
    selectPrivateComments,
    selectReviewComments,
    selectScorable,
} from "features/scorable/scorableSlice";
import { ScoreOverview } from "features/scorable/ScoreOverview";
import FormType from "enums/FormType";
import { FormHeader } from "./FormEditor";
import {
    CheckboxModeContext,
    PreviewModeContext,
    ScorableValidationContext,
} from "../common/Contexts";
import { useEffect, useState } from "react";
import {
    SurveyRenderedFooter,
    SurveyRenderedHeader,
} from "features/scorable/Survey";
import { AlertMessage } from "features/scorable/Evaluation/AlertMessage";
import {
    EvaluationContextSwitcherPreview,
    MultipleSelectQuestionToggle,
} from "features/scorable/Evaluation";
import EvaluationReviewCheck from "../formViewer/footer/EvaluationReviewCheck";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";
import { AdminComments, GeneralComments } from "components/Comments/Comments";
import { DefaultLimits } from "constants/DefaultLimits";
import { EvaluationFormEvaluationFormTypeEnum } from "@genesys/cloud-frontend/models/EvaluationForm";
import { selectHasMultipleSelectQuestions } from "features/qualityForm/qualityFormValidations";
import { toGuxDisabled } from "util/componentUtil";

export default function FormViewerPreview() {
    const t = useStrings(strings);

    const dispatch = useAppDispatch();
    const isScorableFinished = useAppSelector((state) => {
        return selectIsScorableComplete(state);
    });
    const [isAdminView, setIsAdminView] = useState(true);
    const [showMSQInCheckbox, setShowMSQInCheckbox] = useState(false);

    const context = isAdminView ? "admin" : "agent";
    const [evaluationReviewedToggle, setEvaluationReviewedToggle] =
        useState(false);
    const hasAgentReviewed = useAppSelector(selectAgentHasRead) ?? false;

    const isSurveyForm = useAppSelector((state) => {
        return selectFormType(state) === FormType.SURVEY;
    });
    const showQuestionGroupsAndIndices = !isSurveyForm || isScorableFinished;
    const formType = useAppSelector(selectFormType);
    const evaluationFormType = useAppSelector(selectEvaluationFormType);
    const submitPreviewLabel = () => {
        if (formType === FormType.EVALUATION) {
            return t.labels.previewSubmitEvaluation;
        } else if (formType === FormType.SURVEY) {
            return t.labels.previewSubmitSurvey;
        } else {
            return t.labels.previewSubmitAssessment;
        }
    };
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const isMultipleSelectFTEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.MULTIPLE_SELECT);
    });
    const hasMultipleSelectQuestion = useAppSelector(
        selectHasMultipleSelectQuestions
    );

    const evaluatorComment = useAppSelector((state) => {
        return selectScorable(state)?.answers?.comments;
    });
    const reviewComment = useAppSelector((state) => {
        return selectReviewComments(state) ?? "";
    });
    const privateComment = useAppSelector((state) => {
        return selectPrivateComments(state) ?? "";
    });

    const showCommentsAndReviewCheck =
        formType === FormType.EVALUATION &&
        evaluationFormType !== EvaluationFormEvaluationFormTypeEnum.Automated;

    const showButtonOperations =
        formType !== FormType.EVALUATION ||
        evaluationFormType !== EvaluationFormEvaluationFormTypeEnum.Automated;

    const showToggleForMultipleSelectQuestion =
        isAdminView &&
        isMultipleSelectFTEnabled &&
        !isScorableFinished &&
        hasMultipleSelectQuestion;

    useEffect(() => {
        if (!hasAgentReviewed) {
            setEvaluationReviewedToggle(false);
        }
    }, [hasAgentReviewed]);

    function handlePreviewSubmit(isAgentView: boolean) {
        dispatch(
            getPreviewScores({
                isScorableFinished: true,
            })
        ).then((e) => {
            if ("error" in e) {
                setShowValidationErrors(true);
                toastService.notifyMinorAlert(
                    `composer.${formType}s.editor.submitValidations.preview.failure.title`,
                    {
                        bodyMessageKey: `composer.${formType}s.editor.submitValidations.preview.failure.message`,
                    }
                );
            } else {
                if (isAgentView) {
                    dispatch(scorableSliceActions.changeAgentReviewed());
                }
                toastService.notifyMessage(
                    `composer.${formType}s.editor.submitValidations.preview.success.title`,
                    {
                        bodyMessageKey: `composer.${formType}s.editor.submitValidations.preview.success.message`,
                        icon: "fa-check-circle",
                    },
                    false
                );
            }
        });
    }

    return (
        <PreviewModeContext.Provider value={{ previewMode: true }}>
            <div className="FormViewerPreview">
                {formType === FormType.EVALUATION && isScorableFinished && (
                    <AlertMessage isAdmin={isAdminView}>
                        {isScorableFinished && (
                            <EvaluationContextSwitcherPreview
                                context={context}
                                toggleRoleView={() => {
                                    setIsAdminView(!isAdminView);
                                    if (
                                        evaluatorComment &&
                                        evaluatorComment?.length >
                                            DefaultLimits.generalCommentCharLimit
                                    ) {
                                        dispatch(
                                            scorableSliceActions.fillEvaluatorComments(
                                                ""
                                            )
                                        );
                                    }
                                    if (
                                        !hasAgentReviewed ||
                                        reviewComment?.length >
                                            DefaultLimits.generalCommentCharLimit
                                    ) {
                                        dispatch(
                                            scorableSliceActions.fillReviewComments(
                                                ""
                                            )
                                        );
                                    }
                                    if (
                                        privateComment &&
                                        privateComment?.length >
                                            DefaultLimits.generalCommentCharLimit
                                    ) {
                                        dispatch(
                                            scorableSliceActions.fillPrivateComments(
                                                ""
                                            )
                                        );
                                    }
                                }}
                            />
                        )}
                    </AlertMessage>
                )}
                <FormHeader />
                {formType === FormType.SURVEY && <SurveyRenderedHeader />}
                {showToggleForMultipleSelectQuestion && (
                    <MultipleSelectQuestionToggle
                        showMSQInCheckbox={showMSQInCheckbox}
                        setShowMSQInCheckbox={setShowMSQInCheckbox}
                    />
                )}
                <ScorableValidationContext.Provider
                    value={{ showValidationErrors }}
                >
                    {showQuestionGroupsAndIndices ? (
                        showToggleForMultipleSelectQuestion ? (
                            <CheckboxModeContext.Provider
                                value={{ checkboxMode: showMSQInCheckbox }}
                            >
                                <ScorableQuestionGroupList />
                            </CheckboxModeContext.Provider>
                        ) : (
                            <ScorableQuestionGroupList />
                        )
                    ) : (
                        <ScorableQuestionGroupListSurveys />
                    )}
                </ScorableValidationContext.Provider>
                <Footer>
                    {formType === FormType.SURVEY && <SurveyRenderedFooter />}
                    {showCommentsAndReviewCheck && (
                        <>
                            {isAdminView ? (
                                <AdminComments isPreviewMode={true} />
                            ) : (
                                <GeneralComments
                                    context={context}
                                    rescoreMode={false}
                                />
                            )}
                            {isScorableFinished && (
                                <EvaluationReviewCheck
                                    isAdmin={isAdminView}
                                    onChange={() => {
                                        setEvaluationReviewedToggle(
                                            !evaluationReviewedToggle
                                        );
                                    }}
                                    evaluationReviewed={
                                        hasAgentReviewed ||
                                        (!isAdminView &&
                                            evaluationReviewedToggle)
                                    }
                                />
                            )}
                        </>
                    )}
                    {!isScorableFinished ? (
                        <div
                            className={`PreviewSubmitButton ${
                                evaluationFormType ===
                                EvaluationFormEvaluationFormTypeEnum.Automated
                                    ? "with-margin"
                                    : ""
                            }`}
                        >
                            <GuxButton
                                accent="secondary"
                                onClick={() => handlePreviewSubmit(false)}
                            >
                                <GuxIcon icon-name="badge-check" decorative />
                                <span>{submitPreviewLabel()}</span>
                            </GuxButton>
                        </div>
                    ) : (
                        showButtonOperations && (
                            <div
                                className="button-operations"
                                data-testid="button-operations"
                            >
                                {isAdminView ? (
                                    <GuxButton
                                        accent="secondary"
                                        onClick={() => {
                                            setEvaluationReviewedToggle(false);
                                            setIsAdminView(true);
                                            dispatch(
                                                scorableSliceActions.updateEvaluationStatusToInProgress()
                                            );
                                        }}
                                        data-testid="revise-score-button"
                                    >
                                        <span>{t.labels.reviseScore}</span>
                                    </GuxButton>
                                ) : (
                                    <>
                                        {!hasAgentReviewed && (
                                            <div className="save-operation">
                                                <GuxButton
                                                    accent="primary"
                                                    onClick={() =>
                                                        handlePreviewSubmit(
                                                            true
                                                        )
                                                    }
                                                    disabled={toGuxDisabled(
                                                        !evaluationReviewedToggle
                                                    )}
                                                    aria-disabled={toGuxDisabled(
                                                        !evaluationReviewedToggle
                                                    )}
                                                >
                                                    {t.labels.signOff}
                                                </GuxButton>
                                                <GuxButton
                                                    accent="secondary"
                                                    onClick={() => {
                                                        setEvaluationReviewedToggle(
                                                            false
                                                        );
                                                        setIsAdminView(true);
                                                        dispatch(
                                                            scorableSliceActions.fillReviewComments(
                                                                ""
                                                            )
                                                        );
                                                    }}
                                                >
                                                    {t.labels.cancel}
                                                </GuxButton>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )
                    )}
                </Footer>
                <ScoreOverview />
            </div>
        </PreviewModeContext.Provider>
    );
}
